"use client";

import { clsx } from "clsx";
import { createContext, useContext, useState } from "react";
import { Link } from "react-router";
import LoadingText from "../LoadingText";

const TableContext = createContext({
  bleed: false,
  dense: false,
  grid: false,
  striped: false,
});

export function Table({
  bleed = false,
  dense = false,
  grid = false,
  striped = false,
  className,
  children,
  ...props
}) {
  return (
    <TableContext.Provider value={{ bleed, dense, grid, striped }}>
      <div className="flow-root">
        <div
          {...props}
          className={clsx(className, "-mx-[--gutter] whitespace-nowrap ")}
        >
          <div
            className={clsx(
              "inline-block min-w-full align-middle",
              !bleed && "sm:px-[--gutter]",
              "max-w-full",
            )}
          >
            <table className="min-w-full text-left text-sm/6 text-zinc-950 dark:text-white">
              {children}
            </table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  );
}

export function TableHead({ className, ...props }) {
  return (
    <thead
      {...props}
      className={clsx(className, "text-zinc-500 dark:text-zinc-400")}
    />
  );
}

export function TableBody({ className, ...props }) {
  return <tbody {...props} className={clsx(className, "max-w-full")} />;
}

const TableRowContext = createContext({
  href: undefined,
  target: undefined,
  title: undefined,
});

export function TableRow({
  href,
  target,
  title,
  className,
  onClick,
  ...props
}) {
  let { striped } = useContext(TableContext);
  const handleClick = () => {
    if (!event.target.closest("button")) {
      if (onClick) {
        onClick();
      }
    }
  };

  return (
    <TableRowContext.Provider value={{ href, target, title }}>
      <tr
        {...props}
        onClick={handleClick}
        className={clsx(
          className,
          href || onClick ? "cursor-pointer" : "",
          href &&
            "has-[[data-row-link][data-focus]]:outline has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-blue-500 dark:focus-within:bg-white/[2.5%]",
          striped && "even:bg-zinc-950/[2.5%] dark:even:bg-white/[2.5%]",
          (href || onClick) &&
            striped &&
            "hover:bg-zinc-950/5 dark:hover:bg-white/5",
          (href || onClick) &&
            !striped &&
            "hover:bg-zinc-950/[2.5%] dark:hover:bg-white/[2.5%]",
        )}
      />
    </TableRowContext.Provider>
  );
}

export function TableHeader({ className, ...props }) {
  let { bleed, grid } = useContext(TableContext);

  return (
    <th
      {...props}
      className={clsx(
        className,
        "border-b border-b-zinc-950/10 px-4 py-2 font-medium first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))] dark:border-b-white/10 sticky top-0 bg-white z-10 bg-opacity-75 backdrop-blur backdrop-filter border-grey",
        grid &&
          "border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5 sticky top-0 bg-white z-10 bg-opacity-75 backdrop-blur backdrop-filter border-b border-grey",
        !bleed && "sm:first:pl-1 sm:last:pr-1",
      )}
    />
  );
}

export function TableCell({ className, children, ...props }) {
  // Utilisation des contextes et de useState pour gérer les états et les styles
  let { bleed, dense, grid, striped } = useContext(TableContext);
  let { href } = useContext(TableRowContext);
  let [cellRef, setCellRef] = useState(null);

  return (
    <td
      ref={href ? setCellRef : undefined}
      {...props}
      className={clsx(
        className,
        "relative px-4 first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))]",
        !striped && "border-b border-zinc-950/5 dark:border-white/5",
        grid &&
          "border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5",
        dense ? "py-2.5" : "py-4",
        !bleed && "sm:first:pl-1 sm:last:pr-1",
      )}
    >
      {href && (
        <Link
          data-row-link
          to={href}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className="absolute inset-0 focus:outline-none"
        />
      )}

      {children === undefined ? (
        <LoadingText />
      ) : (
        <span>
          {children !== null && children !== "" && children !== 0
            ? children
            : "-"}
        </span>
      )}
    </td>
  );
}
